import React from 'react';
import AnimatedSection from './AnimatedSection';

const benefits = [
  {
    id: 'entspannung',
    title: 'Entspannung',
    description: 'Lavendel, Kamille und Ylang-Ylang beruhigen Geist und Körper für tiefe Gelassenheit.',
    icon: '🌿',
    image: '/img/lavender-oil.jpg'
  },
  {
    id: 'regeneration',
    title: 'Regeneration',
    description: 'Rosmarin und Eukalyptus fördern die Durchblutung und unterstützen die Muskelregeneration.',
    icon: '🌱',
    image: '/img/rosemary-oil.jpg'
  },
  {
    id: 'energie',
    title: 'Energie',
    description: 'Pfefferminze und Zitrusdüfte beleben die Sinne und schenken neue Vitalität.',
    icon: '✨',
    image: '/img/peppermint-oil.jpg'
  }
];

const OilsRitual = () => {
  return (
    <section
      id="oele-ritual"
      data-testid="oils-section"
      className="py-24 md:py-32 relative overflow-hidden"
    >
      {/* Background with image */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: 'url(/img/background/massage-spa-1.jpg)',
          backgroundAttachment: 'fixed',
          backgroundPosition: 'center',
          backgroundSize: 'cover'
        }}
      />
      <div className="absolute inset-0 bg-brand-dark/95" />

      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-brand-gold/30 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-brand-gold/30 to-transparent" />
      
      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-brand-gold/20 rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${5 + Math.random() * 5}s`
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div className="text-brand-light">
            <AnimatedSection direction="left">
              <span className="inline-flex items-center gap-2 font-work text-sm text-brand-gold uppercase tracking-widest mb-4">
                <span className="w-8 h-px bg-brand-gold" />
                Premium Aroma
              </span>
              
              <h2 className="font-marcellus text-4xl md:text-5xl lg:text-6xl mb-6">
                Öle & <span className="gradient-text">Ritual</span>
              </h2>
            </AnimatedSection>
            
            <AnimatedSection direction="left" delay={100}>
              <p className="font-work text-brand-light/70 text-lg leading-relaxed mb-6">
                Meine handverlesenen ätherischen Öle werden aus den reinsten Quellen bezogen. 
                Jedes Aromaöl-Ritual beginnt mit einer persönlichen Duftberatung, um die 
                perfekte Mischung für Ihre Bedürfnisse zu finden.
              </p>
              
              <p className="font-work text-brand-light/60 leading-relaxed mb-10">
                Die Kombination aus professioneller Massagetechnik und aromatherapeutischen 
                Wirkstoffen schafft ein ganzheitliches Wellness-Erlebnis, das Körper und 
                Seele in Einklang bringt.
              </p>
            </AnimatedSection>

            {/* Features */}
            <AnimatedSection direction="left" delay={200}>
              <div className="flex flex-wrap gap-4">
                {['100% natürlich', 'Premium Qualität', 'Individuell gemischt'].map((tag, i) => (
                  <span key={i} className="px-4 py-2 rounded-full bg-brand-gold/10 border border-brand-gold/20 text-brand-gold font-work text-sm">
                    {tag}
                  </span>
                ))}
              </div>
            </AnimatedSection>
          </div>

          {/* Benefit Cards */}
          <div className="space-y-6">
            {benefits.map((benefit, index) => (
              <AnimatedSection key={benefit.id} direction="right" delay={index * 150}>
                <div
                  data-testid={`oil-benefit-${benefit.id}`}
                  className="group flex items-center gap-6 p-4 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:border-brand-gold/30 transition-all duration-500"
                >
                  {/* Image */}
                  <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 img-zoom">
                    <img 
                      src={benefit.image} 
                      alt={benefit.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl">{benefit.icon}</span>
                      <h3 className="font-marcellus text-xl text-brand-light group-hover:text-brand-gold transition-colors">
                        {benefit.title}
                      </h3>
                    </div>
                    <p className="font-work text-sm text-brand-light/60 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>

                  {/* Arrow */}
                  <div className="text-brand-gold/0 group-hover:text-brand-gold transition-all duration-300 transform translate-x-0 group-hover:translate-x-2">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </AnimatedSection>
            ))}

            {/* CTA Card */}
            <AnimatedSection direction="right" delay={500}>
              <a
                href="#buchung"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#buchung')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="group flex items-center justify-between p-6 rounded-2xl bg-brand-gold text-brand-dark hover:bg-white transition-all duration-300"
              >
                <div>
                  <p className="font-marcellus text-xl mb-1">Aromaöl-Ritual buchen</p>
                  <p className="font-work text-sm opacity-70">Ab €109 · 60/90 Min</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-brand-dark/10 flex items-center justify-center group-hover:bg-brand-dark/20 transition-colors">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>
              </a>
            </AnimatedSection>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OilsRitual;
